import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getProviderAnalytics, getCompletionStats, getPortfolioAnalytics, getFunnelStats, getTimeSpentStats } from '@/services/bookingApi';
import type { BookingAnalytics, PortfolioAnalytics, FunnelStats, TimeSpentStats } from '@/types';
import {
  Calendar,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
  Eye,
  Users,
  Timer,
  ArrowDown,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  TrendingDown,
} from 'lucide-react';

type DateRange = '7d' | '30d' | '90d' | 'all';

const getDateRangeParams = (range: DateRange): { startDate?: string; endDate?: string } => {
  const endDate = new Date();
  const startDate = new Date();

  switch (range) {
    case '7d':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(startDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(startDate.getDate() - 90);
      break;
    case 'all':
      return { startDate: undefined, endDate: undefined };
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const formatSeconds = (seconds: number | undefined | null): string => {
  if (seconds == null || isNaN(seconds)) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
};

export default function Analytics() {
  const { t } = useTranslation('dashboard');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const { startDate, endDate } = getDateRangeParams(dateRange);

  // Fetch main booking analytics
  const { data: analytics, isLoading: isAnalyticsLoading } = useQuery<BookingAnalytics>({
    queryKey: ['providerAnalytics', startDate, endDate],
    queryFn: () => getProviderAnalytics(startDate, endDate),
  });

  // Fetch completion stats
  const { data: completionStats, isLoading: isCompletionStatsLoading } = useQuery({
    queryKey: ['completionStats'],
    queryFn: () => getCompletionStats(),
  });

  // Fetch portfolio analytics
  const { data: portfolioAnalytics, isLoading: isPortfolioLoading } = useQuery<PortfolioAnalytics>({
    queryKey: ['portfolioAnalytics', startDate, endDate],
    queryFn: () => getPortfolioAnalytics(startDate, endDate),
  });

  // Fetch funnel stats
  const { data: funnelStats, isLoading: isFunnelLoading } = useQuery<FunnelStats>({
    queryKey: ['funnelStats', startDate, endDate],
    queryFn: () => getFunnelStats(startDate, endDate),
  });

  // Fetch time spent stats
  const { data: timeSpentStats, isLoading: isTimeSpentLoading } = useQuery<TimeSpentStats>({
    queryKey: ['timeSpentStats', startDate, endDate],
    queryFn: () => getTimeSpentStats(startDate, endDate),
  });

  const isLoading = isAnalyticsLoading || isCompletionStatsLoading;

  const stats = [
    {
      label: t('analytics.totalBookings') || 'Total Bookings',
      value: analytics?.total_bookings ?? 0,
      icon: Calendar,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      label: t('analytics.completionRate') || 'Completion Rate',
      value: analytics?.completion_rate ? `${Math.round(analytics.completion_rate)}%` : '0%',
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      progress: analytics?.completion_rate ?? 0,
    },
    {
      label: t('analytics.avgCompletionTime') || 'Avg Completion Time',
      value: analytics?.average_completion_time_minutes
        ? `${Math.round(analytics.average_completion_time_minutes)} min`
        : '0 min',
      icon: Clock,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      label: t('analytics.activeBookings') || 'Active Bookings',
      value: (analytics?.total_bookings ?? 0) - (analytics?.completed_bookings ?? 0) - (analytics?.cancelled_bookings ?? 0),
      icon: Activity,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
    },
  ];

  // Portfolio stats cards
  const portfolioStats = [
    {
      value: portfolioAnalytics?.total_views ?? 0,
      icon: Eye,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-50',
    },
    {
      label: t('analytics.uniqueVisitors') || 'Unique Visitors',
      value: portfolioAnalytics?.unique_visitors ?? 0,
      icon: Users,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50',
    },
  ];

  // Time spent stats
  const timeSpentData = [
    {
      label: t('analytics.avgTimeOnPortfolio') || 'Avg Time on Portfolio',
      value: timeSpentStats ? formatSeconds(timeSpentStats.avg_time_on_portfolio_seconds) : '-',
      icon: Timer,
      color: 'text-teal-500',
      bgColor: 'bg-teal-50',
    },
    {
      label: t('analytics.avgTimeOnBooking') || 'Avg Time on Booking',
      value: timeSpentStats ? formatSeconds(timeSpentStats.avg_time_on_booking_flow_seconds) : '-',
      icon: Clock,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
    },
  ];

  const dateRangeOptions: { value: DateRange; label: string }[] = [
    { value: '7d', label: t('analytics.last7Days') || 'Last 7 days' },
    { value: '30d', label: t('analytics.last30Days') || 'Last 30 days' },
    { value: '90d', label: t('analytics.last90Days') || 'Last 90 days' },
    { value: 'all', label: t('analytics.allTime') || 'All time' },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('analytics.title') || 'Analytics'}
          </h1>
          <p className="mt-1 text-gray-500">
            {t('analytics.subtitle') || 'Track your business performance'}
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {dateRangeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setDateRange(option.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dateRange === option.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards - Bookings */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{stat.label}</p>
                    <p className="text-2xl font-semibold">{isLoading ? '-' : stat.value}</p>
                  </div>
                  {stat.progress !== undefined && (
                    <ProgressRing progress={stat.progress} />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary Cards - Portfolio Views */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {portfolioStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{stat.label}</p>
                    <p className="text-2xl font-semibold">
                      {isPortfolioLoading ? '-' : stat.value}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {/* Time Spent Stats */}
        {timeSpentData.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{stat.label}</p>
                    <p className="text-2xl font-semibold">
                      {isTimeSpentLoading ? '-' : stat.value}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Booking Funnel Section */}
      <div className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.bookingFunnel') || 'Booking Funnel'}</CardTitle>
          </CardHeader>
          <CardContent>
            {isFunnelLoading ? (
              <div className="h-32 flex items-center justify-center">
                <div className="animate-pulse text-gray-400">Loading...</div>
              </div>
            ) : funnelStats ? (
              <BookingFunnelWidget stats={funnelStats} />
            ) : (
              <div className="h-32 flex items-center justify-center text-gray-400">
                No funnel data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.deviceBreakdown') || 'Device Breakdown'}</CardTitle>
          </CardHeader>
          <CardContent>
            {isPortfolioLoading ? (
              <div className="h-48 flex items-center justify-center">
                <div className="animate-pulse text-gray-400">Loading...</div>
              </div>
            ) : portfolioAnalytics ? (
              <DeviceBreakdownChart data={portfolioAnalytics.device_breakdown} />
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400">
                No device data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Referrers */}
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.topReferrers') || 'Top Referrers'}</CardTitle>
          </CardHeader>
          <CardContent>
            {isPortfolioLoading ? (
              <div className="h-48 flex items-center justify-center">
                <div className="animate-pulse text-gray-400">Loading...</div>
              </div>
            ) : portfolioAnalytics && portfolioAnalytics.top_referrers.length > 0 ? (
              <TopReferrersList referrers={portfolioAnalytics.top_referrers} />
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400">
                No referrer data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Completion Time Chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.completionTimeTitle') || 'Completion Time Distribution'}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-48 flex items-center justify-center">
                <div className="animate-pulse text-gray-400">Loading...</div>
              </div>
            ) : completionStats ? (
              <CompletionTimeChart stats={completionStats} />
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ProgressRing({ progress }: { progress: number }) {
  const radius = 18;
  const strokeWidth = 3;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
      <circle
        cx="22"
        cy="22"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-gray-200"
      />
      <circle
        cx="22"
        cy="22"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        className="text-green-500 transition-all duration-500"
      />
    </svg>
  );
}

function CompletionTimeChart({ stats }: { stats: BookingAnalytics['completion_time_stats'] }) {
  if (!stats) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-400">
        No data available
      </div>
    );
  }
  const maxValue = Math.max(stats.max ?? 1, 1);
  const bars = [
    { label: 'Min', value: stats.min ?? 0, color: 'bg-blue-400' },
    { label: 'Avg', value: stats.avg ?? 0, color: 'bg-green-500' },
    { label: 'Max', value: stats.max ?? 0, color: 'bg-purple-500' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-around h-40 gap-4">
        {bars.map((bar) => (
          <div key={bar.label} className="flex flex-col items-center flex-1">
            <div className="w-full flex flex-col items-center">
              <span className="text-sm font-medium text-gray-600 mb-2">
                {Math.round(bar.value)} min
              </span>
              <div className="w-full max-w-16 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`${bar.color} rounded-full transition-all duration-500`}
                  style={{ height: `${Math.max((bar.value / maxValue) * 100, 5)}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 mt-2">{bar.label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center text-sm text-gray-500">
        Based on {stats.count} completed bookings
      </div>
    </div>
  );
}

function BookingFunnelWidget({ stats }: { stats: FunnelStats }) {
  const stages = [
    { label: 'Page Views', value: stats.started_bookings + stats.completed_bookings, icon: Eye },
    { label: 'Started', value: stats.started_bookings, icon: Activity },
    { label: 'Completed', value: stats.completed_bookings, icon: CheckCircle },
  ];

  return (
    <div className="space-y-6">
      {/* Funnel Visualization */}
      <div className="flex items-center justify-between gap-4">
        {stages.map((stage, index) => {
          const Icon = stage.icon;
          const isLast = index === stages.length - 1;

          return (
            <div key={stage.label} className="flex-1 flex flex-col items-center">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{stage.label}</span>
              </div>
              <div
                className={`w-full rounded-lg py-4 px-2 text-center transition-all duration-500 ${
                  isLast ? 'bg-green-100' : 'bg-blue-50'
                }`}
                style={{ minWidth: '100px' }}
              >
                <p className={`text-xl font-bold ${isLast ? 'text-green-700' : 'text-blue-700'}`}>
                  {stage.value}
                </p>
              </div>
              {index < stages.length - 1 && (
                <div className="flex items-center justify-center my-2">
                  <ArrowDown className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats Row */}
      <div className="flex justify-center gap-8 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-red-500" />
          <span className="text-sm text-gray-600">
            Drop-off Rate: <span className="font-semibold text-red-600">{!isNaN(stats.drop_off_rate) ? Math.round(stats.drop_off_rate) : 0}%</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-green-500" />
          <span className="text-sm text-gray-600">
            Conversion Rate: <span className="font-semibold text-green-600">{!isNaN(stats.conversion_rate) ? Math.round(stats.conversion_rate) : 0}%</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function DeviceBreakdownChart({ data }: { data: PortfolioAnalytics['device_breakdown'] }) {
  const total = data.mobile + data.desktop + data.tablet;
  const devices = [
    { label: 'Mobile', value: data.mobile, percentage: total > 0 ? (data.mobile / total) * 100 : 0, icon: Smartphone, color: 'bg-pink-500' },
    { label: 'Desktop', value: data.desktop, percentage: total > 0 ? (data.desktop / total) * 100 : 0, icon: Monitor, color: 'bg-blue-500' },
    { label: 'Tablet', value: data.tablet, percentage: total > 0 ? (data.tablet / total) * 100 : 0, icon: Tablet, color: 'bg-purple-500' },
  ];

  const maxValue = Math.max(data.mobile, data.desktop, data.tablet, 1);

  return (
    <div className="space-y-4">
      {/* Horizontal Bar Chart */}
      <div className="space-y-3">
        {devices.map((device) => {
          const Icon = device.icon;
          return (
            <div key={device.label} className="flex items-center gap-3">
              <div className="w-24 flex items-center gap-2">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">{device.label}</span>
              </div>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div
                  className={`${device.color} h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                  style={{ width: `${Math.max((device.value / maxValue) * 100, 2)}%` }}
                >
                  <span className="text-xs font-medium text-white">{Math.round(device.percentage)}%</span>
                </div>
              </div>
              <div className="w-16 text-right text-sm font-medium text-gray-700">
                {device.value}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center text-sm text-gray-500 pt-2">
        Total: {total} sessions
      </div>
    </div>
  );
}

function TopReferrersList({ referrers }: { referrers: PortfolioAnalytics['top_referrers'] }) {
  const top5 = referrers.slice(0, 5);
  const maxValue = Math.max(top5[0]?.count ?? 1, 1);

  return (
    <div className="space-y-3">
      {top5.map((referrer, index) => (
        <div key={referrer.referrer} className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 truncate">
                <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">
                  {referrer.referrer || 'Direct'}
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900 ml-2">
                {referrer.count}
              </span>
            </div>
            <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${(referrer.count / maxValue) * 100}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
